<template>
    <v-container fluid class="pa-0">
        <filter-panel
            v-if="filterOptions"
            :filterOptions="filterOptions"
            :model-value="filterState"
            @update:modelValue="onUpdateFilters"
        />
        <v-data-table
            :headers="orderHeaders"
            :items="filteredOrders"
            item-key="id"
            class="elevation-1"
            :items-per-page="25"
            :search="search"
        >
            <template v-slot:item.productionType="{ item }">
                <span>{{ item.productionType }}, {{ getFolderName(item.benchlingFolder) }}</span>
            </template>
            <template v-slot:item.quantity="{ item }">
                {{ formatQuantity(item) }}
            </template>
            <template v-slot:item.submittedAt="{ item }">
                {{ getISODateOnlyStringInLocalTimeZone(item.submittedAt) }}
            </template>
            <template v-slot:item.requiredByDate="{ item }">
                {{ getDateOnlyValue(item.requiredByDate) }}
            </template>
            <!-- allow inline editing of the status column -->
            <template v-slot:item.status="{ item }">
                <div v-if="editedItem?.id !== item.id">
                    <span @click="startEdit(item)" class="editable-text">{{ item.status }}</span>
                </div>
                <div v-else>
                    <v-autocomplete
                        v-model="editedItem.status"
                        :items="statusOptions"
                        density="compact"
                        variant="outlined"
                        label="Edit Status"
                        class="mt-2"
                    ></v-autocomplete>
                    <v-btn
                        class="mb-1"
                        size="small"
                        variant="text"
                        color="blue"
                        @click="saveEdit(item)"
                        >Save</v-btn
                    >
                    <v-btn class="mb-1" size="small" variant="text" color="red" @click="cancelEdit"
                        >Cancel</v-btn
                    >
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <!-- download button -->
                <v-icon-btn
                    color="primary"
                    @click="downloadOrderDetails(item)"
                    :id="`downloadOrderDetails${item.id}`"
                    density="comfortable"
                >
                    <v-icon>$table-arrow-down</v-icon>
                </v-icon-btn>
                <v-tooltip location="right" :activator="`#downloadOrderDetails${item.id}`">
                    <span>Download Order Summary</span>
                </v-tooltip>
                <!-- upload button -->
                <v-icon-btn
                    v-if="showOrderUploadButton(item)"
                    color="primary"
                    @click="openOrderDetailsUploadDialog(item)"
                    :id="`uploadOrderDetails${item.id}`"
                    density="comfortable"
                >
                    <v-icon>$table-arrow-up</v-icon>
                </v-icon-btn>
                <v-tooltip location="right" :activator="`#uploadOrderDetails${item.id}`">
                    <span>Upload Order Details</span>
                </v-tooltip>
                <!-- report order item failure button -->
                <v-icon-btn
                    v-if="item.productionType === ProductionType.MICROBIAL"
                    color="primary"
                    @click="openReportOrderItemFailureDialog(item)"
                    :id="`reportOrderItemFailure${item.id}`"
                    density="comfortable"
                >
                    <v-icon>$clipboard-edit-outline</v-icon>
                </v-icon-btn>
                <v-tooltip location="right" :activator="`#reportOrderItemFailure${item.id}`">
                    <span>Report cloning or synthesis failure</span>
                </v-tooltip>
                <!-- edit order button -->
                <v-icon-btn
                    color="primary"
                    @click="openEditOrderDialog(item)"
                    :id="`editOrder${item.id}`"
                    density="comfortable"
                >
                    <v-icon>$pencil</v-icon>
                </v-icon-btn>
                <v-tooltip location="right" :activator="`#editOrder${item.id}`">
                    <span>Edit Order</span>
                </v-tooltip>
                <!-- delete order button -->
                <v-icon-btn
                    color="red"
                    @click="deleteOrder(item)"
                    :id="`deleteOrder${item.id}`"
                    density="comfortable"
                >
                    <v-icon>$trash-can-outline</v-icon>
                </v-icon-btn>
                <v-tooltip location="right" :activator="`#deleteOrder${item.id}`">
                    <span>Delete Order</span>
                </v-tooltip>
            </template>
        </v-data-table>
        <v-dialog
            v-model="showOrderDetailsUploadDialog"
            max-width="600px"
            @click:outside="closeOrderDetailsUploadDialog"
            @keyup.escape="closeOrderDetailsUploadDialog"
        >
            <v-card>
                <v-card-title>Upload Order Details</v-card-title>
                <v-card-text>
                    <v-file-input
                        v-model="orderDetailsFile"
                        accept=".xlsx"
                        label="Select Order Details File"
                        variant="outlined"
                    ></v-file-input>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" :disabled="!orderDetailsFile" @click="uploadOrderDetails"
                        >Upload</v-btn
                    >
                    <v-btn color="red" @click="closeOrderDetailsUploadDialog">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>
<script setup lang="ts">
import { buildFilterOptionsFromData, FilterConfigItem, filterData, FilterOptions } from "@/lib/filterUtils";
import FilterPanel from "@/components/common/FilterPanel.vue";
import { getISODateOnlyStringInLocalTimeZone, getDateOnlyValue } from "@/lib/dateUtils";
import { Order, ordersAPI, OrderStatus, ProductionType, SavedOrder } from "@/services/orders/ordersAPI";
import { withLoadingScreen } from "@/lib/loading";
import { computed, ref, watch } from "vue";
import { downloadFile } from "@/lib/downloadFileUtil";
import { useToast } from "vue-toastification";
import _ from "lodash";
import { FolderOption } from "@/services/commonModels";
import { VDataTableHeader } from "@/vuetifyTypeFixes";

const toast = useToast();

interface Props {
    search: string | undefined;
    orders: SavedOrder[];
    folders: FolderOption[];
}

const props = withDefaults(defineProps<Props>(), {
    search: "",
});

const editedItem = ref<SavedOrder | null>(null);
const showOrderDetailsUploadDialog = ref(false);
const orderDetailsFile = ref<File | null>(null);
const uploadOrderDetailsItem = ref<SavedOrder | null>(null);

const filterState = ref(<Record<string, string[]>>{});

const filterConfig = ref<FilterConfigItem[]>([
    { name: "status", prop: "status" },
    { name: "submittedBy", prop: "submittedBy" },
    { name: "productionType", prop: "productionType" },
]);

const filterOptions = ref<FilterOptions>({});

const orderHeaders = ref<VDataTableHeader[]>([
    { title: "Order ID", value: "orderCode", sortable: true, align: "start", width: "6%" },
    { title: "Order Name", value: "displayName", sortable: true, align: "start", width: "14%" },
    { title: "Production Type", value: "productionType", sortable: true, align: "start", width: "14%" },
    { title: "# of Peptides", value: "orderItemsCount", sortable: true, align: "start", width: "2%" },
    { title: "Submitted On", value: "submittedAt", sortable: true, align: "start", width: "6%" },
    { title: "Submitted By", value: "submittedBy", sortable: true, align: "start", width: "10%" },
    { title: "Required By", value: "requiredByDate", sortable: true, align: "start", width: "6%" },
    { title: "Quantity Needed", value: "quantity", sortable: true, align: "start", width: "7%" },
    { title: "Target Purity", value: "targetPurity", sortable: true, align: "start", width: "5%" },
    { title: "Status", value: "status", sortable: true, align: "start", width: "11%" },
    { title: "Actions", value: "actions", sortable: false, align: "start", width: "15%" },
]);

const statusOptions = [
    OrderStatus.SUBMITTED,
    OrderStatus.IN_PROGRESS,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELED,
];

const filteredOrders = computed(() => {
    return filterData(props.orders, filterState.value, filterConfig.value);
});

const emit = defineEmits<{
    (e: "orderUpdated"): void;
    (e: "orderDeleted", item: SavedOrder): void;
    (e: "editOrder", item: SavedOrder): void;
    (e: "reportOrderItemFailure", item: SavedOrder): void;
}>();

watch(
    () => props.orders,
    (newOrders) => {
        filterOptions.value = buildFilterOptionsFromData(newOrders, filterConfig.value);
    },
    { immediate: true }
);

function onUpdateFilters(newFilters: Record<string, string[]>) {
    filterState.value = newFilters;
}

function startEdit(item: SavedOrder) {
    editedItem.value = { ...item }; // clone item to avoid direct mutation
}

async function saveEdit(item: SavedOrder) {
    if (!editedItem.value) return;
    await withLoadingScreen(async () => {
        const updatedOrder = { ...item, status: editedItem.value!.status };
        await ordersAPI.updatePeptideOrder(updatedOrder);
        emit("orderUpdated");
        editedItem.value = null; // exit edit mode
    });
}

function cancelEdit() {
    editedItem.value = null; // discard changes
}

function formatQuantity(item: Order) {
    const quantityNeeded = item.quantityNeeded || "";
    const quantityNeededUnits = item.quantityNeededUnits || "";
    const netGrossSuffix = item.quantityNetGross ? ` (${item.quantityNetGross})` : "";
    return `${quantityNeeded} ${quantityNeededUnits}${netGrossSuffix}`;
}

function getFolderName(folderId: string): string {
    const folder = props.folders.find((folder) => folder.id === folderId);
    return folder ? folder.name : "";
}

async function downloadOrderDetails(item: SavedOrder) {
    await withLoadingScreen(async () => {
        const response = await ordersAPI.downloadOrderDetails(item.id);
        const filename = `${item.orderCode} Summary.xlsx`;
        downloadFile(filename, response);
    });
}

function openOrderDetailsUploadDialog(item: SavedOrder) {
    uploadOrderDetailsItem.value = item;
    showOrderDetailsUploadDialog.value = true;
    orderDetailsFile.value = null;
}

function closeOrderDetailsUploadDialog() {
    showOrderDetailsUploadDialog.value = false;
    orderDetailsFile.value = null;
    uploadOrderDetailsItem.value = null;
}

async function deleteOrder(item: SavedOrder) {
    if (confirm(`Are you sure you want to delete order ${item.orderCode}?`)) {
        await withLoadingScreen(async () => {
            await ordersAPI.deletePeptideOrder(item.id);
            emit("orderDeleted", item);
        });
    }
}

function openEditOrderDialog(item: SavedOrder) {
    emit("editOrder", item);
}

function openReportOrderItemFailureDialog(item: SavedOrder) {
    emit("reportOrderItemFailure", item);
}

async function uploadOrderDetails() {
    if (!uploadOrderDetailsItem.value || !orderDetailsFile.value) return;
    try {
        await withLoadingScreen(async () => {
            const response = await ordersAPI.uploadOrderDetails(
                uploadOrderDetailsItem.value!.id,
                orderDetailsFile.value
            );
            const message =
                response.skippedRowMessages.length > 0
                    ? `Order details uploaded successfully. Skipped updating status for ${response.skippedRowMessages} row(s).`
                    : "Order details uploaded successfully.";
            toast.success(message);
        });
    } catch (error) {
        const message = _.get(error, "response.data.error") || "Failed to upload order details";
        toast.error(message);
    } finally {
        closeOrderDetailsUploadDialog();
    }
}

function showOrderUploadButton(item: Order): boolean {
    return (
        item.productionType !== ProductionType.MICROBIAL ||
        getFolderName(item.benchlingFolder) !== "Discovery Platform"
    );
}
</script>

<style scoped>
.editable-text {
    cursor: pointer;
    text-decoration: underline;
}
</style>
